import type { CustomerOrder } from '../../customerOrdersApi'

export type BillingFormState = {
  firstName: string
  lastName: string
  taxCode: string
  phone: string
  billingEmail: string
}

export const BILLING_PREFERENCES_STORAGE_PREFIX = 'emporion.customer.billing-preferences.v1'
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
export const TAX_CODE_REGEX = /^[A-Z0-9]{16}$/

export const parseTimestamp = (value: string | null | undefined) => {
  if (!value) return 0
  const timestamp = new Date(value).getTime()
  return Number.isFinite(timestamp) ? timestamp : 0
}

export const readBooleanPreference = (storageKey: string, fallback: boolean) => {
  if (typeof window === 'undefined') return fallback
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (raw === 'true') return true
    if (raw === 'false') return false
    return fallback
  } catch {
    return fallback
  }
}

export const writeBooleanPreference = (storageKey: string, value: boolean): boolean => {
  if (typeof window === 'undefined') return false
  try {
    window.localStorage.setItem(storageKey, value ? 'true' : 'false')
    return true
  } catch {
    return false
  }
}

export const LocationIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} stroke="currentColor" strokeWidth={1.8} aria-hidden>
    <path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11Z" />
    <circle cx="12" cy="10" r="2.8" />
  </svg>
)

export const LockIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} stroke="currentColor" strokeWidth={1.8} aria-hidden>
    <rect x="4" y="10" width="16" height="10" rx="2.5" />
    <path d="M8 10V7.8A4 4 0 0 1 12 4a4 4 0 0 1 4 3.8V10" />
  </svg>
)

export const CurrencyIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} stroke="currentColor" strokeWidth={1.8} aria-hidden>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M14.8 8.5h-3.5a2.3 2.3 0 0 0 0 4.6h1.6a2.3 2.3 0 0 1 0 4.6H9.2M12 7v10" />
  </svg>
)

export const CalendarIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} stroke="currentColor" strokeWidth={1.8} aria-hidden>
    <rect x="3.5" y="5.5" width="17" height="15" rx="2.5" />
    <path d="M3.5 9.5h17M8 3.5v4M16 3.5v4" />
  </svg>
)

export const StatusIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} stroke="currentColor" strokeWidth={1.8} aria-hidden>
    <path d="M5 12.5 9.2 16.7 19 7" />
  </svg>
)

export const OrderIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} stroke="currentColor" strokeWidth={1.8} aria-hidden>
    <path d="M4 7.5h16v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-11Zm0 0 2.5-4h11L20 7.5" />
    <path d="M9 11.5h6" />
  </svg>
)

export const resolveOrderStatusLabel = (status: CustomerOrder['status'], language: 'it' | 'en') => {
  if (language === 'en') {
    if (status === 'created') return 'Processing'
    if (status === 'paid') return 'Paid'
    if (status === 'shipped') return 'Shipped'
    return 'Cancelled'
  }

  if (status === 'created') return 'In lavorazione'
  if (status === 'paid') return 'Pagato'
  if (status === 'shipped') return 'Spedito'
  return 'Annullato'
}

export const resolveOrderStatusBadgeClass = (status: CustomerOrder['status']) => {
  if (status === 'paid') return 'ca-badge-success'
  if (status === 'shipped') return 'ca-badge-info'
  if (status === 'cancelled') return 'ca-badge-error'
  return 'ca-badge-warning'
}
